export type CountryEntry = string | string[];

/** ISO alpha-2 → label(s); same shape as `GET /countries` on the auth API. */
export type CountriesMap = Record<string, CountryEntry>;

export const COUNTRY_NONE = "__country_none__";

function primaryLabel(entry: CountryEntry): string {
  return Array.isArray(entry) ? entry[0] : entry;
}

function entryMatchesName(entry: CountryEntry, normalizedLowerName: string): boolean {
  if (!normalizedLowerName) return false;
  if (Array.isArray(entry)) return entry.some(e => e.trim().toLowerCase() === normalizedLowerName);
  return entry.trim().toLowerCase() === normalizedLowerName;
}

export function buildCountryOptions(countries: CountriesMap, showFirst: string[]): { code: string; label: string }[] {
  const priority = showFirst.map(code => code.trim().toUpperCase()).filter(Boolean);
  const priorityIndex = new Map(priority.map((code, index) => [code, index]));

  const options = Object.entries(countries).map(([code, entry]) => ({
    code,
    label: primaryLabel(entry),
  }));

  const preferred: typeof options = [];
  const other: typeof options = [];

  for (const option of options) {
    if (priorityIndex.has(option.code.toUpperCase())) {
      preferred.push(option);
    } else {
      other.push(option);
    }
  }

  preferred.sort(
    (a, b) => priorityIndex.get(a.code.toUpperCase())! - priorityIndex.get(b.code.toUpperCase())!
  );
  other.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  return [...preferred, ...other];
}

/** Normalizes stored country to an ISO alpha-2 code from the given map, or "". */
export function resolveCountryCode(countries: CountriesMap, stored: string | undefined): string {
  if (!stored?.trim()) return "";
  const t = stored.trim();
  const upper = t.toUpperCase();
  if (countries[upper] !== undefined) return upper;
  const asName = t.toLowerCase();
  for (const [code, entry] of Object.entries(countries)) {
    if (entryMatchesName(entry, asName)) return code;
  }
  return "";
}

export function isKnownCountryCode(countries: CountriesMap, code: string | undefined): boolean {
  if (!code?.trim()) return false;
  return countries[code.trim().toUpperCase()] !== undefined;
}
